import React from 'react';

/**
 * Dépouillement du vote du jury : règles, participation, podium, classement.
 *
 * Partagé par la page Jury de l'administration et par l'onglet « Résultat » de
 * l'espace jury. Les deux doivent montrer EXACTEMENT le même classement — une
 * seconde copie de ce bloc finirait par diverger, et deux écrans annonceraient
 * deux podiums. Ce qui est réservé à l'organisateur (export, clôture, détail
 * nominatif par juré) reste en dehors, chez l'appelant.
 *
 * `results` est la réponse du serveur ; côté jury elle ne contient ni `members`
 * ni `detail` ni `favorites`, d'où les accès prudents.
 */
export default function JuryResultsBoard({ results, error }) {
  if (error) {
    return (
      <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FFEBEE',
        color: '#C62828', fontSize: 13 }}>{error}</div>
    );
  }
  if (!results) return <div className="loading">Chargement du dépouillement...</div>;

  const budget = results.budget_per_juror || 15;
  const laureates = results.ranking.filter(r => r.total > 0);
  const top = results.ranking[0]?.total || 1;
  const favorites = results.favorites || [];

  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16 }}>
        Chaque juré répartit <strong>{budget} M€ fictifs</strong> sur 3 à 5 startups de la
        sélection. Les trois startups ayant reçu le plus de capital sont lauréates ;
        à montant égal, celle soutenue par le plus grand nombre de jurés passe devant,
        puis, à égalité encore, celle dont le <strong>score ISI</strong> est le plus élevé.
      </p>

      {results.locked && (
        <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8, fontSize: 13,
          background: '#E8F5E9', border: '1px solid #C8E6C9', color: '#2E7D32' }}>
          <strong>Vote clôturé</strong> le {new Date(results.locked_at).toLocaleString('fr-FR')} —
          les portefeuilles sont figés.
        </div>
      )}

      {/* Voix d'anciens membres : conservées, mais hors comptage. Les taire
          ferait un écart inexplicable entre ce tableau et les portefeuilles
          réellement enregistrés. */}
      {results.excluded?.jurors > 0 && (
        <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8, fontSize: 13,
          background: '#FFF8E1', border: '1px solid #FFE0A3', color: '#8D6E20' }}>
          <strong>{results.excluded.invested} M€ ne sont pas comptés</strong> dans ce
          dépouillement : ils proviennent de {results.excluded.jurors} juré
          {results.excluded.jurors > 1 ? 's' : ''} retiré
          {results.excluded.jurors > 1 ? 's' : ''} du jury de cette campagne. Leur
          portefeuille est conservé : les rattacher de nouveau à la campagne leur
          rendrait leur voix.
        </div>
      )}

      {/* Participation : un classement établi sur la moitié des jurés ne se lit
          pas comme un classement complet. */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-value">{results.totals.jurors_complete}<span
            style={{ fontSize: 16, color: 'var(--text-light)' }}> / {results.totals.jurors}</span></div>
          <div className="stat-label">Portefeuilles complets</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#C8880A' }}>{results.totals.invested} M€</div>
          <div className="stat-label">Capital engagé sur {results.totals.expected} M€</div>
        </div>
        {/* Coup de cœur abandonné par le jury : la liste revient vide et ce bloc
            ne s'affiche pas. S'il est rétabli (COUP_DE_COEUR_ACTIF dans
            jury.py), il réapparaît de lui-même. */}
        {favorites.length > 0 && (
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#E74C3C' }}>
              {favorites.reduce((s, f) => s + f.votes, 0)}
            </div>
            <div className="stat-label">Coups de cœur exprimés</div>
          </div>
        )}
      </div>

      {/* Podium : les trois lauréates, lisibles sans parcourir le tableau. */}
      {laureates.length > 0 && (
        <div className="resp-4col" style={{ gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12, marginBottom: 20 }}>
          {laureates.slice(0, 3).map((r, i) => (
            <div key={r.startup_id} style={{ padding: 16, borderRadius: 12, textAlign: 'center',
              border: `2px solid ${['#F4C542', '#C0C6D4', '#CD7F32'][i]}`,
              background: ['rgba(244,197,66,0.10)', 'rgba(192,198,212,0.12)', 'rgba(205,127,50,0.10)'][i] }}>
              <div style={{ fontSize: 26 }}>{['🥇', '🥈', '🥉'][i]}</div>
              <div style={{ fontWeight: 700, fontSize: 15, margin: '4px 0' }}>{r.name}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#C8880A' }}>{r.total} M€</div>
              <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                {r.backers} juré{r.backers > 1 ? 's' : ''} · ticket max {r.max_ticket} M€
              </div>
              {r.tie_broken && (
                <div style={{ fontSize: 11, color: 'var(--primary)', marginTop: 4 }}>
                  départagée au score ISI
                  {r.note_sectorielle != null && ` (${r.note_sectorielle.toFixed(2)})`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="table-scroll" style={{ marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', width: 46 }}>Rang</th>
              <th style={{ textAlign: 'left', padding: '8px 10px' }}>Startup</th>
              <th style={{ textAlign: 'right', padding: '8px 10px' }}>Capital</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', width: '30%' }}>Répartition</th>
              <th style={{ textAlign: 'right', padding: '8px 10px' }}>Jurés</th>
              <th style={{ textAlign: 'right', padding: '8px 10px' }}>Ticket max</th>
              <th style={{ textAlign: 'right', padding: '8px 10px' }}>Ticket moyen</th>
              <th style={{ textAlign: 'right', padding: '8px 10px' }}>Score ISI</th>
            </tr>
          </thead>
          <tbody>
            {results.ranking.map(r => (
              <tr key={r.startup_id} style={{ borderBottom: '1px solid var(--border)',
                background: r.rank && r.rank <= 3 ? 'rgba(244,197,66,0.07)' : 'transparent' }}>
                <td style={{ padding: '8px 10px', fontWeight: 700 }}>
                  {r.total > 0 ? (r.rank <= 3 ? ['🥇', '🥈', '🥉'][r.rank - 1] : `#${r.rank}`) : '—'}
                </td>
                <td style={{ padding: '8px 10px', fontWeight: 600 }}>
                  {r.name}
                  {/* Un rang gagné autrement qu'au capital doit pouvoir
                      s'expliquer : on dit lequel, plutôt que de laisser deviner
                      pourquoi deux montants égaux ne sont pas au même rang. */}
                  {r.tie_broken && (
                    <span title="À égalité de capital et de nombre de jurés, départagée au score ISI"
                      style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 600,
                        padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap',
                        background: 'rgba(74,113,229,0.12)', color: 'var(--primary)' }}>
                      départagée au score ISI
                    </span>
                  )}
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800,
                  color: r.total > 0 ? '#C8880A' : 'var(--text-light)' }}>
                  {r.total} M€
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ height: 10, background: 'var(--bg-secondary)', borderRadius: 5,
                    overflow: 'hidden' }}>
                    <div style={{ width: `${(r.total / top) * 100}%`, height: '100%',
                      borderRadius: 5, background: 'linear-gradient(90deg, #E0A106, #F4C542)' }} />
                  </div>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.backers}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.max_ticket || '—'}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.avg_ticket || '—'}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right',
                  color: r.note_sectorielle == null ? 'var(--text-light)'
                    : r.note_sectorielle > 7 ? '#27AE60' : '#F39C12', fontWeight: 600 }}>
                  {r.note_sectorielle == null ? '—' : r.note_sectorielle.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
